import { logger } from '../core/logger';
import { MicroFrontEndConfig } from '../environments/environment.model';

type ModuleConstructor = new (options: { target: HTMLElement }) => unknown;

/**
 * Loads a script dynamically into the page
 *
 * @param url URL of the script to load
 * @returns Promise that resolves when the script is loaded
 */
export function loadScript(url: string): Promise<void> {
  const isScriptLoaded = Array.from(document.scripts).some((script) => script.src === url);
  if (isScriptLoaded) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script ${url}`));
    document.head.appendChild(script);
  });
}
/**
 * Loads and initializes a remote microfrontend module
 *
 * @param config Configuration for the microfrontend
 * @returns Promise that resolves with the loaded module
 */

async function loadRemoteModule(config: MicroFrontEndConfig): Promise<ModuleConstructor> {
  try {
    // Load the remote entry script
    await loadScript(config.remoteEntry);

    // Get the container from the window object
    const containerKey = config.remoteName as keyof Window;
    const container = window[containerKey];

    if (!container) {
      throw new Error(`Remote container ${config.remoteName} not found after loading ${config.remoteEntry}`);
    }

    // Initialize the container with an empty shared scope for now
    // In a more complex setup, we would provide actual shared modules here
    // Check if container is already initialized to avoid re-initialization error
    if (!container.__initialized) {
      await container.init({});
      container.__initialized = true;
    }

    // Get the requested module from the container
    const factory = await container.get(config.exposedModule);
    const Module = factory();

    return Module[config.moduleId ?? 'default'];
  } catch (error) {
    logger.error(`Failed to load remote module ${config.exposedModule} from ${config.remoteEntry}:`, error);
    throw error;
  }
}
/**
 * Loads a microfrontend and mounts it into a container
 *
 * @param microFrontEnds Array of available microfrontends
 * @param name Name of the microfrontend to load
 * @param container DOM element to mount the microfrontend into
 * @param config Configuration to pass to the microfrontend
 * @returns The microfrontend instance
 */
export function loadMFE(microFrontEnds: MicroFrontEndConfig[], name: string, container: HTMLElement): Promise<unknown> {
  // Find the matching microfrontend config
  const mfeConfig = microFrontEnds.find((mfe) => mfe.rel === name);

  if (!mfeConfig) {
    throw new Error(`Microfrontend with name ${name} not found in environment configuration`);
  }

  // Dynamically load the module
  return loadRemoteModule(mfeConfig)
    .then((Module) => {
      // Create and mount the component
      const instance = new Module({
        target: container
      });

      logger.info(`Successfully loaded microfrontend: ${name}`);
      return instance;
    })
    .catch((error) => {
      logger.error(`Failed to load microfrontend ${name}:`, error);
      // Display error in container
      container.innerHTML = /* html */ `
        <div style="color: red; padding: 20px; border: 1px solid red; border-radius: 5px;">
            <h3>Error loading component</h3>
            <p>${error.message}</p>
        </div>
      `;
      throw error;
    });
}
